// Canonical lifecycle UI per ADR-0005 for the CHILD content tiers (exhibit +
// zone): reads `state`, dispatches useTransitionExhibit / useTransitionZone
// with {to_state: "published"|"draft"}. Generalised from the exhibit-only
// PublishToggle (FU-1) so both child forms share one publish control instead
// of ZoneForm having none.
//
// F-C: archive lives at the exhibition tier only (Q-OQ-A), so an exhibit or a
// zone can only reach ARCHIVED / PURGED via the exhibition's cascade. When it
// does, this toggle renders read-only and drops the action button — the admin
// must restore the parent exhibition to bring the child back.
//
// The exhibition tier is intentionally NOT wired here: it's the cascade root,
// not a child, and keeps its own bespoke Lifecycle controls (publish +
// archive / restore / export / purge together in ExhibitionForm).

import type { FC } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@shared/components/ui/button";
import {
  useExhibit,
  useTransitionExhibit,
} from "@shared/api/hooks/useExhibits";
import { useStaffZone, useTransitionZone } from "@shared/api/hooks/useZones";
import { ApiError } from "@shared/api/client";

type PublishOwnerKind = "exhibit" | "zone";
type LifecycleState = "draft" | "published" | "archived" | "purged";

const STATE_LABEL: Record<LifecycleState, string> = {
  draft: "Draft",
  published: "Published",
  archived: "▼ Archived",
  purged: "✕ Purged",
};

// Capitalised for toasts ("Zone published"); lower-cased inline for the hint.
const NOUN: Record<PublishOwnerKind, string> = {
  exhibit: "Exhibit",
  zone: "Zone",
};

export interface PublishToggleProps {
  ownerKind: PublishOwnerKind;
  entityId: string;
}

const PublishToggle: FC<PublishToggleProps> = ({ ownerKind, entityId }) => {
  // Both entity queries + both transition mutations are declared
  // unconditionally (Rules of Hooks). The inactive owner's query is disabled
  // via an undefined id (enabled:false, no fetch), and only the active owner's
  // mutation is dispatched below.
  const exhibitQuery = useExhibit(
    ownerKind === "exhibit" ? entityId : undefined,
  );
  const zoneQuery = useStaffZone(ownerKind === "zone" ? entityId : undefined);
  const transitionExhibit = useTransitionExhibit();
  const transitionZone = useTransitionZone();

  const entity = ownerKind === "exhibit" ? exhibitQuery.data : zoneQuery.data;
  const isLoading =
    ownerKind === "exhibit" ? exhibitQuery.isLoading : zoneQuery.isLoading;
  const isTransitioning =
    ownerKind === "exhibit"
      ? transitionExhibit.isPending
      : transitionZone.isPending;

  if (isLoading || !entity) {
    return (
      <div
        className="p-4 bg-white border border-border rounded-lg text-sm text-muted-foreground"
        data-testid="publish-toggle"
      >
        Loading status…
      </div>
    );
  }

  const currentState = entity.state;
  const isPublished = currentState === "published";
  const isPurged = currentState === "purged";
  const isLocked = isPurged || currentState === "archived";
  const nextState: "draft" | "published" = isPublished ? "draft" : "published";
  const noun = NOUN[ownerKind];
  const nounLower = noun.toLowerCase();

  const handleToggle = () => {
    const handlers = {
      onSuccess: () => {
        toast.success(
          isPublished ? `${noun} unpublished` : `${noun} published`,
        );
      },
      onError: (err: unknown) => {
        // Surface lifecycle gate violations (e.g. missing_title_tc)
        // verbatim — they're already user-facing per PR 5's error envelope.
        const message =
          err instanceof ApiError && err.message
            ? err.message
            : "Failed to update publish status";
        toast.error(message);
      },
    };
    if (ownerKind === "exhibit") {
      transitionExhibit.mutate(
        { exhibitId: entityId, payload: { to_state: nextState } },
        handlers,
      );
    } else {
      transitionZone.mutate(
        { zoneId: entityId, payload: { to_state: nextState } },
        handlers,
      );
    }
  };

  return (
    <div
      className="space-y-2 p-4 bg-white border border-border rounded-lg"
      data-testid="publish-toggle"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm text-muted-foreground">Status</div>
          <div className="font-medium" data-testid="publish-state-display">
            {STATE_LABEL[currentState]}
          </div>
        </div>
        {isLocked ? (
          <span
            className="text-xs text-muted-foreground"
            data-testid="publish-toggle-archived-hint"
          >
            {isPurged
              ? `This ${nounLower} has been purged — bytes removed; only a tombstone remains.`
              : `Restore the parent exhibition to bring this ${nounLower} back.`}
          </span>
        ) : (
          <Button
            type="button"
            onClick={handleToggle}
            disabled={isTransitioning}
            variant={isPublished ? "outline" : "default"}
            data-testid="publish-toggle-button"
          >
            {isTransitioning && (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            )}
            {isPublished ? "Unpublish" : "Publish"}
          </Button>
        )}
      </div>
    </div>
  );
};

export default PublishToggle;
